import { userRoutesPermissionChecker } from '../pmd/composition/userPermission'
import { DashboardRouteNames } from '../routes/constants/location'

// Import types from navigation.ts
interface Permissions {
  all: string[],
  any: string[]
}

interface Scopes {
  all: string[],
  any: string[]
}

interface V2RouteConfig {
  name: string,
  enabled: boolean,
  items: {[key:string]: any}[],
  type: string,
  icon: string | any,
  tooltip: string,
  children: {[key:string]: any}[],
  permissions: Permissions,
  userScopes: Scopes,
  route: {[key:string]: any},
  url?: string,
  mode?: string,
  linkId?: string,
  meta: string,
  reference_route?: {[key: string]: any},
  hide?: boolean,
  link? : {
    locations: string[],
    show_on_location: boolean,
    show_on_company: boolean
  },
  hideForCompanyId?: string,
  showOnlyForCompanyId?: string
}

interface NavigationFallbackContext {
  locationPermissions: { [key: string]: boolean }
  userScopes: string[]
  hideLaunchpad?: boolean
  store?: any
  route?: {
    params: { [key: string]: any }
    query: { [key: string]: any }
  }
}

interface FallbackResult {
  routeName: string
  params: { [key: string]: any }
  query?: { [key: string]: any }
  replace?: boolean
}

/**
 * Checks if a navigation item has the required permissions
 */
function hasNavigationPermissions(
  navItem: V2RouteConfig,
  context: NavigationFallbackContext
): boolean {
  const { locationPermissions, userScopes, hideLaunchpad, store } = context

  // Check location-level permissions
  if (navItem.permissions) {
    const { all: allPermissions, any: anyPermissions } = navItem.permissions
    
    // Check "all" permissions - all must be enabled
    if (allPermissions.length > 0) {
      const hasAllPermissions = allPermissions.every(permission => 
        locationPermissions[permission] !== false
      )
      if (!hasAllPermissions) return false
    }

    // Check "any" permissions - at least one must be enabled
    if (anyPermissions.length > 0) {
      const hasAnyPermission = anyPermissions.some(permission => 
        locationPermissions[permission] !== false
      )
      if (!hasAnyPermission) return false
    }
  }

  // Check user-level scopes
  if (navItem.userScopes) {
    const { all: allScopes, any: anyScopes } = navItem.userScopes
    
    // Check "all" scopes - user must have all required scopes
    if (allScopes.length > 0) {
      const hasAllScopes = allScopes.every(scope => 
        userScopes.includes(scope) || scope === 'dashboard/stats.readonly'
      )
      if (!hasAllScopes) return false
    }

    // Check "any" scopes - user must have at least one required scope
    if (anyScopes.length > 0) {
      const hasAnyScope = anyScopes.some(scope => userScopes.includes(scope))
      if (!hasAnyScope) return false
    }
  }

  // Special permission checks for specific routes
  if (navItem.route?.name) {
    const routeName = navItem.route.name

    // Check launchpad permissions
    if (routeName === 'launchpad-default') {
      const { checkLaunchpadPermissions } = userRoutesPermissionChecker()
      return checkLaunchpadPermissions(locationPermissions, userScopes, hideLaunchpad || false)
    }

    // Check forms permissions
    if (routeName === 'form-builder-main-v2') {
      const { checkFormsPermissions } = userRoutesPermissionChecker()
      return checkFormsPermissions(locationPermissions, userScopes)
    }

    // Check quizzes permissions
    if (routeName === 'quiz-builder-main') {
      const { checkQuizzesPermissions } = userRoutesPermissionChecker()
      return checkQuizzesPermissions(locationPermissions, userScopes)
    }

    // Check surveys permissions
    if (routeName === 'survey-builder-main') {
      const { checkSurveysPermissions } = userRoutesPermissionChecker()
      return checkSurveysPermissions(locationPermissions, userScopes)
    }

    // Check QR codes permissions
    if (routeName === 'qr-codes') {
      const { checkQrCodesPermissions } = userRoutesPermissionChecker()
      return checkQrCodesPermissions(locationPermissions, userScopes)
    }

    // Check marketing permissions
    if (navItem.name === 'common.sidebar.marketing') {
      const targetPermissions = [
        'marketing.write',
        'socialplanner/post.write',
        'marketing/affiliate.write',
        'adPublishing.write',
        'prospecting.write'
      ]
      return targetPermissions.some(target => userScopes.includes(target))
    }

    // Check contacts permissions
    if (routeName === 'contacts-v2') {
      return userScopes.includes('contacts.write')
    }

    // Check bulk actions permissions
    if (navItem.permissions?.all?.includes('bulk_requests_enabled')) {
      return userScopes.includes('contacts/bulkActions.write')
    }

    // Check team management permissions
    if (['agency-team-settings', 'my-staff'].includes(navItem.meta)) {
      return userScopes.includes('users/team-management.readonly')
    }
  }

  return true
}

/**
 * Recursively finds the first accessible route, checking children routes
 */
function findFirstAccessibleRoute(
  navItem: V2RouteConfig,
  context: NavigationFallbackContext
): V2RouteConfig | null {
  // Check if current item has permissions
  if (!hasNavigationPermissions(navItem, context)) {
    return null
  }

  // If this item has a direct route and is enabled, check children first
  if (navItem.enabled && navItem.children && navItem.children.length > 0) {
    // Look for accessible children first
    for (const child of navItem.children) {
      const accessibleChild = findFirstAccessibleRoute(child as V2RouteConfig, context)
      if (accessibleChild && accessibleChild.route?.name) {
        return accessibleChild
      }
    }
  }

  // If no accessible children found, return this item if it has a route
  if (navItem.enabled && navItem.route?.name) {
    return navItem
  }

  return null
}

/**
 * Gets the first available route from navigation based on permissions
 * Now properly handles children routes
 */
export function getFirstAvailableRoute(
  navigationItems: V2RouteConfig[], 
  context: NavigationFallbackContext
): FallbackResult {
  const { route } = context

  // Iterate through navigation items and find first accessible route (including children)
  for (const navItem of navigationItems) {
    const accessibleRoute = findFirstAccessibleRoute(navItem, context)
    if (accessibleRoute && accessibleRoute.route?.name) {
      console.log(`[NavigationFallback] Found accessible route: ${accessibleRoute.route.name} (from parent: ${navItem.name})`)
      return {
        routeName: accessibleRoute.route.name,
        params: route?.params || {},
        query: route?.query,
        replace: true
      }
    }
  }

  // Fallback to dashboard if no other routes are available
  console.log(`[NavigationFallback] No accessible routes found, falling back to dashboard`)
  return {
    routeName: DashboardRouteNames.default,
    params: route?.params || {},
    query: route?.query,
    replace: true
  }
}

/**
 * Recursively searches for a failed route in navigation items and their children
 */
function findFailedRouteIndex(
  navigationItems: V2RouteConfig[],
  failedRouteName: string
): { parentIndex: number; isChild: boolean; childPath?: number[] } {
  for (let i = 0; i < navigationItems.length; i++) {
    const navItem = navigationItems[i]
    
    // Check if this is the failed route
    if (navItem.route?.name === failedRouteName) {
      return { parentIndex: i, isChild: false }
    }
    
    // Check children recursively
    if (navItem.children && navItem.children.length > 0) {
      for (let j = 0; j < navItem.children.length; j++) {
        const child = navItem.children[j] as V2RouteConfig
        if (child.route?.name === failedRouteName) {
          return { parentIndex: i, isChild: true, childPath: [j] }
        }
        
        // Check nested children (if any)
        if (child.children && child.children.length > 0) {
          for (let k = 0; k < child.children.length; k++) {
            const nestedChild = child.children[k] as V2RouteConfig
            if (nestedChild.route?.name === failedRouteName) {
              return { parentIndex: i, isChild: true, childPath: [j, k] }
            }
          }
        }
      }
    }
  }
  
  return { parentIndex: -1, isChild: false }
}

/**
 * Gets a fallback route for a specific failed route
 * This function will find the next available route in the navigation order
 * Now properly handles children routes
 */
export function getFallbackRoute(
  failedRouteName: string, 
  navigationItems: V2RouteConfig[],
  context: NavigationFallbackContext
): FallbackResult {
  const { route } = context

  console.log(`[NavigationFallback] Finding fallback for failed route: ${failedRouteName}`)

  // Find where the failed route is located (parent or child)
  const failedRouteLocation = findFailedRouteIndex(navigationItems, failedRouteName)
  
  let remainingRoutes: V2RouteConfig[] = []
  
  if (failedRouteLocation.parentIndex >= 0) {
    if (failedRouteLocation.isChild) {
      // Failed route is a child - start from the next sibling or next parent
      const parentItem = navigationItems[failedRouteLocation.parentIndex]
      const childPath = failedRouteLocation.childPath!
      
      // Try to find next sibling in the same parent first
      if (parentItem.children && childPath.length === 1) {
        const remainingSiblings = parentItem.children.slice(childPath[0] + 1)
        for (const sibling of remainingSiblings) {
          const accessibleSibling = findFirstAccessibleRoute(sibling as V2RouteConfig, context)
          if (accessibleSibling?.route?.name) {
            console.log(`[NavigationFallback] Found next sibling: ${accessibleSibling.route.name}`)
            return {
              routeName: accessibleSibling.route.name,
              params: route?.params || {},
              query: route?.query,
              replace: true
            }
          }
        }
      }
      
      // No accessible siblings, continue with next parents
      remainingRoutes = navigationItems.slice(failedRouteLocation.parentIndex + 1)
    } else {
      // Failed route is a parent - start from the next parent
      remainingRoutes = navigationItems.slice(failedRouteLocation.parentIndex + 1)
    }
  } else {
    // Failed route not found in navigation, start from beginning
    remainingRoutes = navigationItems
  }

  // Find the first available route from the remaining routes (including children)
  for (const navItem of remainingRoutes) {
    const accessibleRoute = findFirstAccessibleRoute(navItem, context)
    if (accessibleRoute?.route?.name && accessibleRoute.route.name !== failedRouteName) {
      console.log(`[NavigationFallback] Found fallback route: ${accessibleRoute.route.name} (from parent: ${navItem.name})`)
      return {
        routeName: accessibleRoute.route.name,
        params: route?.params || {},
        query: route?.query,
        replace: true
      }
    }
  }

  // If no route found in remaining routes, check from the beginning
  for (const navItem of navigationItems) {
    const accessibleRoute = findFirstAccessibleRoute(navItem, context)
    if (accessibleRoute?.route?.name && accessibleRoute.route.name !== failedRouteName) {
      console.log(`[NavigationFallback] Found fallback route from beginning: ${accessibleRoute.route.name} (from parent: ${navItem.name})`)
      return {
        routeName: accessibleRoute.route.name,
        params: route?.params || {},
        query: route?.query,
        replace: true
      }
    }
  }

  // Ultimate fallback to dashboard
  console.log(`[NavigationFallback] No accessible routes found, using dashboard fallback`)
  return {
    routeName: DashboardRouteNames.default,
    params: route?.params || {},
    query: route?.query,
    replace: true
  }
}

/**
 * Helper to create navigation context from store and route
 */
export function createNavigationContext(store: any, route: any): NavigationFallbackContext {
  const locationPermissions = store?.state?.locations?.currentLocation?.permissions || {}
  const userScopes = store.getters['user/getScopes'] || []
  const hideLaunchpad = store?.state?.company?.company?.hide_launchpad || false

  return {
    locationPermissions,
    userScopes,
    hideLaunchpad,
    store,
    route: {
      params: route.params || {},
      query: route.query || {}
    }
  }
}